/**
 * IAM Management Layout Component
 * Hierarchical navigation similar to mTLS: User Pools → User Pool Management → Details
 */

import React, { useState } from 'react';
import IamUserPoolList from './IamUserPoolList';
import IamUserPoolManagement from './IamUserPoolManagement';
// TODO: Implement these components when needed
// import IamAppClientDetails from './IamAppClientDetails';
// import IamUserDetails from './IamUserDetails';

interface IamLayoutProps {
  // No props needed currently
}

type ViewType = 'user-pools' | 'user-pool-management' | 'app-client-details' | 'user-details';

interface NavigationState {
  view: ViewType;
  userPoolId?: string;
  userPoolName?: string;
  userPoolEnvironment?: string;
  appClientId?: string;
  appClientName?: string;
  username?: string;
}

const IamLayout: React.FC<IamLayoutProps> = () => {
  const [navigation, setNavigation] = useState<NavigationState>({
    view: 'user-pools'
  });

  const navigateToUserPoolManagement = (userPoolId: string, userPoolName: string, userPoolEnvironment?: string) => {
    setNavigation({
      view: 'user-pool-management',
      userPoolId,
      userPoolName,
      userPoolEnvironment
    });
  };

  const navigateToAppClientDetails = (appClientId: string, appClientName: string) => {
    setNavigation({
      ...navigation,
      view: 'app-client-details',
      appClientId,
      appClientName
    });
  };

  const navigateToUserDetails = (username: string) => {
    setNavigation({
      ...navigation,
      view: 'user-details',
      username
    });
  };

  const navigateBack = () => {
    switch (navigation.view) {
      case 'user-details':
      case 'app-client-details':
        setNavigation({
          view: 'user-pool-management',
          userPoolId: navigation.userPoolId,
          userPoolName: navigation.userPoolName
        });
        break;
      case 'user-pool-management':
        setNavigation({ view: 'user-pools' });
        break;
      default:
        setNavigation({ view: 'user-pools' });
    }
  };

  const renderBreadcrumb = () => {
    const breadcrumbs = [
      { label: 'Home', href: '/workloads' },
      { label: 'IAM M2M', active: navigation.view === 'user-pools' }
    ];

    if (navigation.userPoolName) {
      breadcrumbs.push({
        label: navigation.userPoolName,
        active: navigation.view === 'user-pool-management'
      });
    }

    if (navigation.appClientName) {
      breadcrumbs.push({
        label: navigation.appClientName,
        active: navigation.view === 'app-client-details'
      });
    }

    if (navigation.username) {
      breadcrumbs.push({
        label: navigation.username,
        active: navigation.view === 'user-details'
      });
    }

    return (
      <ol className="breadcrumb float-sm-right">
        {breadcrumbs.map((crumb, index) => (
          <li key={index} className={`breadcrumb-item ${crumb.active ? 'active' : ''}`}>
            {crumb.href ? (
              <a href={crumb.href}>{crumb.label}</a>
            ) : crumb.active ? (
              crumb.label
            ) : (
              <button
                type="button"
                className="btn btn-link p-0 border-0 text-primary"
                onClick={navigateBack}
                style={{ textDecoration: 'none' }}
              >
                {crumb.label}
              </button>
            )}
          </li>
        ))}
      </ol>
    );
  };

  const renderContent = () => {
    switch (navigation.view) {
      case 'user-pools':
        return (
          <IamUserPoolList
            onSelectUserPool={navigateToUserPoolManagement}
          />
        );
      
      case 'user-pool-management':
        return (
          <IamUserPoolManagement
            userPoolId={navigation.userPoolId!}
            userPoolName={navigation.userPoolName!}
            userPoolEnvironment={navigation.userPoolEnvironment || 'unknown'}
            onViewAppClient={navigateToAppClientDetails}
            onViewUser={navigateToUserDetails}
            onBack={navigateBack}
          />
        );
      
      case 'app-client-details':
        // TODO: Implement IamAppClientDetails component
        return (
          <div className="alert alert-info">
            <h4>App Client Details</h4>
            <p>This feature is coming soon. App Client ID: {navigation.appClientId}</p>
            <button className="btn btn-secondary" onClick={navigateBack}>
              <i className="fas fa-arrow-left mr-2"></i>Back
            </button>
          </div>
        );
      
      case 'user-details':
        // TODO: Implement IamUserDetails component
        return (
          <div className="alert alert-info">
            <h4>User Details</h4>
            <p>This feature is coming soon. Username: {navigation.username}</p>
            <button className="btn btn-secondary" onClick={navigateBack}>
              <i className="fas fa-arrow-left mr-2"></i>Back
            </button>
          </div>
        );
      
      default:
        return <div>Unknown view</div>;
    }
  };

  return (
    <div className="iam-layout">
      {/* Navigation Header */}
      {navigation.view !== 'user-pools' && (
        <div className="row mb-3">
          <div className="col-12">
            <div className="card card-outline card-primary">
              <div className="card-body py-2">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm mr-3"
                      onClick={navigateBack}
                    >
                      <i className="fas fa-arrow-left mr-1"></i>
                      Back
                    </button>
                    <span className="text-muted">
                      {navigation.view === 'user-pool-management' && `Managing: ${navigation.userPoolName}`}
                      {navigation.view === 'app-client-details' && `App Client: ${navigation.appClientName}`}
                      {navigation.view === 'user-details' && `User: ${navigation.username}`}
                    </span>
                  </div>
                  <nav aria-label="breadcrumb">
                    {renderBreadcrumb()}
                  </nav>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {renderContent()}
    </div>
  );
};

export default IamLayout;